import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Grid2, Typography } from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import CustomAccordion from "../../../../components/ui/materialui/customAccordion";
import InputFecha from "../../../../components/inputs/yup/materialui/inputFecha";
import InputSelect from "../../../../components/inputs/yup/materialui/inputSelect";
import MButton from "../../../../components/inputs/yup/materialui/mButton";
import { FormFiltersInterface } from "../interfaces/FormFiltersInterface"
import InputText from '../../../../components/inputs/yup/materialui/inputText';
import InputSwitch from "../../../../components/inputs/yup/materialui/inputSwitch";
import { selectorTransversal } from "../../../../dtos/selector";
import { AxiosResponse } from "axios";
import { getValoresDominio } from "../../../../services/servicesDominio";
import { Dominios, StatusCode } from "../../../../utils/enums";
import Validaciones from "../../../../utils/validaciones";
import { useValidadorAsientosContablesContext } from "../../../../context/validadorAsientosContablesContext";
import { useLocation } from "react-router-dom";
import { ListResponseDto } from "../../../../dtos/ListResponseDto";

const getFirstDayOfCurrentMonth = () => {
  const today = new Date();
  const fecha = new Date(today.getFullYear(), today.getMonth(), 1);
  fecha.setHours(0,0,0,0);
  return fecha;
};

const getLastDayOfCurrentMonth = () => {
  const today = new Date();
  const fecha = new Date(today.getFullYear(), today.getMonth() + 1, 0); // día 0 del siguiente mes = último día mes actual
  fecha.setHours(0,0,0,0);
  return fecha;
};


const validationSchema: yup.ObjectSchema<FormFiltersInterface> = yup.object().shape({
  byPeriod: yup.boolean().required("El campo es requerido"),
  // Validar periodo solo si byPeriod = true
  periodo: yup.date()
    .when('byPeriod', {
      is: true,
      then: (schema) => schema.required("Seleccione mes y año"),
      otherwise: (schema) => schema.notRequired(),
    }),
  // Validar fechaInicio y fechaFin solo si byPeriod = false
  fechaInicio: yup.date()
    .when('byPeriod', {
      is: false,
      then: (schema) => schema
        .required("Fecha inicio es requerida")
        .test('fechaInicioMenor', 'La fecha inicio debe ser menor que la fecha fin', function (value) {
          const { fechaFin } = this.parent;
          if (!value || !fechaFin) return true; // si uno está vacío, no valida esta regla aquí
          return new Date(value) <= new Date(fechaFin);
        }),
      otherwise: (schema) => schema.notRequired(),
    }),

  fechaFin: yup.date()
    .when('byPeriod', {
      is: false,
      then: (schema) => schema.required("Fecha fin es requerida"),
      otherwise: (schema) => schema.notRequired(),
    }),

  codigoAccion: yup.string().required("La acción contable es requerida"),
  numeroOperacion: yup.string().default('').nullable().test('valida-numero', 'N° de Operacion Inválido',
    function(value) {
      if (!value) return true;
      return Validaciones.esLetrasNumeros(value);
    }
  ),
});

interface FiltrosComponentProps {
  handleFilter: (formFilters: FormFiltersInterface) => void;
}

const getCurrentMonthFirstDay = () => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), 1);
  };

export const FiltrosComponent = ({ handleFilter }: FiltrosComponentProps) => {    

  const { formFilters, setFormFilters } = useValidadorAsientosContablesContext();
  const [options, setOptions] = useState<selectorTransversal[]>([]);
  const location = useLocation();
  let filtersSaved: FormFiltersInterface | null | undefined = location.state;

  const {
    handleSubmit,
    control,
    formState: { errors },
    watch,
    getValues,
    setValue
  } = useForm<FormFiltersInterface>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      byPeriod: true,
      codigoAccion: 'Todas',
      periodo: getCurrentMonthFirstDay(),
      fechaInicio: getFirstDayOfCurrentMonth(),
      fechaFin: getLastDayOfCurrentMonth(),
    },
  });

  const byPeriod = watch("byPeriod");
  const periodo = watch("periodo");
  const fechaInicio = watch("fechaInicio");
  const fechaFin = watch("fechaFin");
  
  const onSubmit = (data: FormFiltersInterface) => handleFilter(data);

  const getTiposAccionContables = async () => {
    const resp: AxiosResponse<ListResponseDto<selectorTransversal>> = await getValoresDominio(Dominios.ACCION_CONTABLE)
    if (resp.status === StatusCode.Ok) {
        const data: selectorTransversal[] = await resp.data.items;
        setOptions(data)
    }
    return;
  };

  useEffect(() => {
    if (byPeriod && periodo) {
      const parsedPeriodo = new Date(periodo);
      if (!isNaN(parsedPeriodo.getTime())) { // Asegurarse de que es válida
        const year = parsedPeriodo.getFullYear();
        const month = parsedPeriodo.getMonth();

        const firstDay = new Date(year, month, 1);
        firstDay.setHours(0, 0, 0, 0);

        const lastDay = new Date(year, month + 1, 0);
        lastDay.setHours(0, 0, 0, 0);

        setValue("fechaInicio", firstDay);
        setValue("fechaFin", lastDay);
      }
    }
  }, [periodo, byPeriod, setValue]);

  const setFiltros = () => {
    if(formFilters?.fechaFin == null) return;
    setValue('byPeriod', formFilters.byPeriod);
    setValue('periodo', formFilters.periodo);
    setValue('fechaInicio', formFilters.fechaInicio);
    setValue('fechaFin', formFilters.fechaFin);
    setValue('codigoAccion', formFilters.codigoAccion);
    setValue('numeroOperacion', formFilters.numeroOperacion);
  }

  useEffect(() => {
    const inicializar = async () => {
      await getTiposAccionContables();
      setFormFilters(getValues());
      setFiltros();
    };
    inicializar();
  }, []);

  return (
    <CustomAccordion title="Filtros" expandedDefault>
      <form onSubmit={handleSubmit(onSubmit)}>

        {/* Fila 1: Radio buton */}
        <Grid2 container spacing={2} sx={{ marginTop: 1, alignItems: "center",}}>
          <Grid2 size={4}>
              <InputSwitch
                name="byPeriod"
                control={control}
                label="Filtrar por periodo"
                error={errors.byPeriod}
              />
          </Grid2>
        </Grid2>

        {/* Fila 2: Fechas */}
        <Grid2 container spacing={2} size={12} sx={{ marginTop: 1 }}>
          { byPeriod ? 
            (
              <>
              
                <Grid2 size={4}>
                  <InputFecha
                    name="periodo"
                    control={control}
                    label="Seleccionar Mes y Año"
                    onlyMonthPicker
                    fecha={periodo}
                  />
                  <Typography color="error">{errors.periodo?.message}</Typography>
                </Grid2>
              </>
            )
            : (
              <>
                <Grid2 size={4}>
                  <InputFecha
                    fecha={fechaInicio}
                    name="fechaInicio"
                    control={control}
                    label="Desde"
                    />
                  <Typography color="error">{errors.fechaInicio?.message}</Typography>
                </Grid2>
                <Grid2 size={4}>
                  <InputFecha
                    fecha={fechaFin}
                    name="fechaFin"
                    control={control}
                    label="Hasta"
                  />
                  <Typography color="error">{errors.fechaFin?.message}</Typography>
                </Grid2>
              </>
            )
          }
        </Grid2>

        {/* Fila 3: Select + input */}
        <Grid2 container spacing={2} size={12} sx={{ marginTop: 1 }}>
          <Grid2 size={6}>
            <InputSelect
              name="codigoAccion"
              control={control}
              error={errors.codigoAccion}
              label="Acción contable"
              options={options}
              clearable={false}
            />
            <Typography color="error">{errors.codigoAccion?.message}</Typography>
          </Grid2>
          <Grid2 size={4}>
            <InputText name="numeroOperacion" control={control} label="N° de Operación" />
            <Typography color="error">{errors.numeroOperacion?.message}</Typography>
          </Grid2> 
        </Grid2>

        {/* Fila 4; Boton para filtrar */}
        <Grid2 size={{ xs: 8 }} sx={{ 
          marginTop: '20px',
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 1,
        }}>
          <MButton
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<FilterListIcon />}
            sx={{ 
              height: "40px", 
              whiteSpace: "nowrap",
              px: 3
            }}
          >
            Analizar
          </MButton>
        </Grid2>
      </form>
    </CustomAccordion>
  );
};